/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-misused-promises */

import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDataFetchingClient } from 'data-fetching-client';
import { List } from 'immutable';
import I18n from 'I18n';
import quickFetch from 'quick-fetch';
import { useDispatch, useSelector } from 'react-redux';
import { useBookingAvailabilityPage } from 'MeetingsLib/hooks/useBookingAvailability';
import { fetchAvailabilityPageQuery } from '../query/bookingAvailabilityQuery';
import LinkAvailabilityRecord from 'MeetingsBase/models/LinkAvailability';
import UserBusyTimesRecord from 'MeetingsBase/models/UserBusyTimes';
import { updateLinkAvailability, updateUserBusyTimes } from 'MeetingsLib/redux/actions/BookActions';
import { getMeetingLength } from 'MeetingsLib/selectors/BookInfoSelectors';
import * as ActionTypes from 'MeetingsLib/redux/actions/ActionTypes';
import { getLinkAvailability } from 'MeetingsLib/selectors/AvailabilitySelectors';
import { jsx as _jsx } from "react/jsx-runtime";
const PublicAvailabilityInfoContext = /*#__PURE__*/createContext({
  currentMonthOffset: 0,
  hasMore: false,
  loading: false,
  availabilityPage: undefined,
  refetch: () => {},
  setCurrentMonthOffset: () => {},
  getNextMonthOffset: () => {},
  getPreviousMonthOffset: () => {},
  buildAvailableStartTimesFromSelectedDate: () => List()
});
export function PublicAvailabilityInfoProvider({
  children,
  slug,
  shouldSkipFirstPage,
  meetingChangeId
}) {
  const [currentMonthOffset, setCurrentMonthOffset] = useState(0);
  const dispatch = useDispatch();
  const {
    cache
  } = useDataFetchingClient();

  // @ts-expect-error unexpected field on state
  const bookingLifeCycle = useSelector(state => state.bookingLifecycle);
  // @ts-expect-error unexpected field on state
  const userTimezone = useSelector(state => state.userTimezone);
  // @ts-expect-error __hsUserToken does not exist on window
  const hubspotUtk = window.__hsUserToken;
  const contactOwnerPrioritizedEmail = useSelector(
  // @ts-expect-error untyped state
  state => state.emailFormState);
  const meetingLength = useSelector(getMeetingLength);
  const existingLinkAvailability = useSelector(getLinkAvailability);
  useEffect(() => {
    if (contactOwnerPrioritizedEmail) {
      quickFetch.removeEarlyRequest('availability-page');
    }
  }, [contactOwnerPrioritizedEmail]);
  const previousTimezone = useRef(userTimezone);
  const hasTimezoneChanged = useMemo(() => {
    return previousTimezone.current !== userTimezone;
  }, [userTimezone]);
  const shouldSkip = useMemo(() => {
    if (hasTimezoneChanged) {
      return false;
    }
    return shouldSkipFirstPage && currentMonthOffset === 0;
  }, [currentMonthOffset, hasTimezoneChanged, shouldSkipFirstPage]);
  const existingLinkAvailabilityHasMore = useMemo(() => {
    return existingLinkAvailability && existingLinkAvailability.get('hasMore');
  }, [existingLinkAvailability]);
  const bookingAvailabilityPageParams = useMemo(() => {
    const params = {
      monthOffset: currentMonthOffset,
      slug,
      hubspotUtk,
      meetingChangeId,
      timezone: userTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      shouldSkip
    };
    if (contactOwnerPrioritizedEmail) {
      params.email = contactOwnerPrioritizedEmail;
    }
    return params;
  }, [currentMonthOffset, slug, hubspotUtk, meetingChangeId, userTimezone, shouldSkip, contactOwnerPrioritizedEmail]);
  const {
    availabilityPage,
    loading,
    hasMore,
    refetch,
    error
  } = useBookingAvailabilityPage(bookingAvailabilityPageParams);
  const buildAvailableStartTimesFromSelectedDate = useCallback(() => {
    const payload = cache.readQuery({
      query: fetchAvailabilityPageQuery,
      variables: {
        slug,
        timezone: userTimezone,
        monthOffset: currentMonthOffset,
        email: contactOwnerPrioritizedEmail || undefined
      }
    });
    if (payload && payload.publicAvailabilityPage) {
      const linkAvailabilityByDuration = payload.publicAvailabilityPage.linkAvailability.linkAvailabilityByDuration;
      if (linkAvailabilityByDuration[meetingLength]) {
        const availableTimesByDate = List(linkAvailabilityByDuration[meetingLength].availabilities.map(availability => availability.startMillisUtc)).groupBy(startMillisUtc => I18n.moment(startMillisUtc).tz(userTimezone).format('YYYY-MM-DD'));
        const startTimesForSelectedDate = availableTimesByDate.get(bookingLifeCycle.event.dateString) || List();
        return startTimesForSelectedDate;
      }
      return List();
    }
    return List();
  }, [bookingLifeCycle.event.dateString, cache, contactOwnerPrioritizedEmail, currentMonthOffset, meetingLength, slug, userTimezone]);
  const resetMonthOffset = useCallback(() => {
    const resetValue = shouldSkipFirstPage ? 1 : 0;
    setCurrentMonthOffset(resetValue);
  }, [shouldSkipFirstPage]);
  const getNextMonthOffset = useCallback(() => {
    if (!loading && existingLinkAvailabilityHasMore) {
      setCurrentMonthOffset(previousOffset => previousOffset + 1);
    }
  }, [existingLinkAvailabilityHasMore, loading]);
  const getPreviousMonthOffset = useCallback(() => {
    if (!loading) {
      setCurrentMonthOffset(previousOffset => previousOffset - 1);
    }
  }, [loading]);
  useEffect(() => {
    if (loading) {
      dispatch({
        type: ActionTypes.PUBLIC_AVAILABILITY_INFO_FETCH_STARTED
      });
    }
  }, [dispatch, loading]);
  useEffect(() => {
    if (availabilityPage) {
      const linkAvailabilityPayload = LinkAvailabilityRecord.from(availabilityPage.linkAvailability);
      dispatch(updateLinkAvailability(linkAvailabilityPayload, `${currentMonthOffset}`));
      const allUsersBusyTimes = List(availabilityPage.allUsersBusyTimes.map(userBusyTimes => UserBusyTimesRecord.from(userBusyTimes)));
      dispatch(updateUserBusyTimes(allUsersBusyTimes));
    }
  }, [availabilityPage, currentMonthOffset, dispatch]);
  useEffect(() => {
    // clear cache to enable refetch of previously seen monthOffsets
    if (bookingLifeCycle.get('event').get('bookingTimeConflictStatus')) {
      cache.evict({
        fieldName: 'publicAvailabilityPage'
      });
      resetMonthOffset();
      dispatch({
        type: ActionTypes.RESET_BOOKING_TIME_CONFLICT_STATUS
      });
    }
  }, [bookingLifeCycle, cache, dispatch, resetMonthOffset]);
  return /*#__PURE__*/_jsx(PublicAvailabilityInfoContext.Provider, {
    value: {
      currentMonthOffset,
      hasMore,
      loading,
      availabilityPage,
      refetch,
      error,
      setCurrentMonthOffset,
      getNextMonthOffset,
      getPreviousMonthOffset,
      buildAvailableStartTimesFromSelectedDate
    },
    children: children
  });
}
export function useSetMonthOffset() {
  const context = useContext(PublicAvailabilityInfoContext);
  return context.setCurrentMonthOffset;
}
export function useCurrentMonthOffset() {
  const context = useContext(PublicAvailabilityInfoContext);
  return context.currentMonthOffset;
}
export function useHasMoreLinkAvailabilityPages() {
  const context = useContext(PublicAvailabilityInfoContext);
  return context.hasMore;
}
export function useIsLoadingLinkAvailabilityPage() {
  const context = useContext(PublicAvailabilityInfoContext);
  return context.loading;
}
export function useNextMonthOffset() {
  const context = useContext(PublicAvailabilityInfoContext);
  return context.getNextMonthOffset;
}
export function usePreviousMonthOffset() {
  const context = useContext(PublicAvailabilityInfoContext);
  return context.getPreviousMonthOffset;
}
export function useAvailabilityPage() {
  const context = useContext(PublicAvailabilityInfoContext);
  return context.availabilityPage;
}
export function useAvailabilityPageRefetch() {
  const context = useContext(PublicAvailabilityInfoContext);
  return context.refetch;
}
export function useHasError() {
  const context = useContext(PublicAvailabilityInfoContext);
  return context.error;
}
export function useAvailableTimesForSelectedDate() {
  const context = useContext(PublicAvailabilityInfoContext);
  return context.buildAvailableStartTimesFromSelectedDate();
}

/**
 * HOC for class components that cannot consume the hooks above
 * directly
 * @param WrappedComponent
 * @returns
 */
export const withPublicAvailabilityInfo = WrappedComponent => {
  const WithPublicAvailabilityInfo = props => {
    const context = useContext(PublicAvailabilityInfoContext);
    return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, props, {
      availabilityContext: context
    }));
  };
  return WithPublicAvailabilityInfo;
};